import { OverlayRef, Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, OnDestroy, OnInit, Input, TemplateRef, ElementRef, ViewContainerRef, HostListener } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TriggerTypePopover } from './popover.enum';
import { PopoverService } from './popover.service';

@Directive({
  selector: '[popoverTrigger]'
})
export class PopoverDirective implements OnDestroy, OnInit {
  @Input() popoverTrigger!: TemplateRef<object>;
  @Input() popoverTriggerOn!: 'hover' | 'click';
  @Input() popoverPanelClass?: string = 'popover-panel-main';

  private _unsubscribeAll$ = new Subject();
  private overlayRef!: OverlayRef;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private popoverService: PopoverService
  ) {}

  @HostListener('mouseenter', ['$event']) onEnter() {
    if (this.popoverTriggerOn === TriggerTypePopover.hover) {
      this.attachOverlay();
    }
  }

  @HostListener('mouseleave', ['$event']) onLeave() {
    if (this.popoverTriggerOn === TriggerTypePopover.hover) {
      this.detachOverlay();
    }
  }

  @HostListener('click') clickou() {
    if (this.popoverTriggerOn === TriggerTypePopover.click) {
      this.attachOverlay();
    }
  }

  ngOnInit(): void {
    this.createOverlay();
    this.popoverService.getState().pipe(takeUntil(this._unsubscribeAll$)).subscribe({
      next: (resp: boolean) => {
        if (resp) {
          this.detachOverlay();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this._unsubscribeAll$.next(true);
    this._unsubscribeAll$.complete();
  }

  private createOverlay(): void {
    const scrollStrategy = this.overlay.scrollStrategies.block();
    this.overlayRef = this.overlay.create({
      scrollStrategy,
      hasBackdrop: ((this.popoverTriggerOn === TriggerTypePopover.click) ? true : false),
      backdropClass: '',
      panelClass: this.popoverPanelClass
    });

    this.overlayRef.backdropClick().pipe(takeUntil(this._unsubscribeAll$)).subscribe({
      next: () => {
        this.detachOverlay();
      }
    });

    const positionStrategy = this.overlay.position().flexibleConnectedTo(this.elementRef).withPositions([
      {  // bottom right
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        panelClass: ['below', 'after']
      },
      { // top right
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        panelClass: ['above', 'after']
      },
      { // bottom left
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        panelClass: ['below', 'before']
      },
      { // top left
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        panelClass: ['above', 'before']
      }
    ]);
    this.overlayRef.updatePositionStrategy(positionStrategy);
  }

  private attachOverlay(): void {
    if (!this.overlayRef.hasAttached()) {
      const periodSelectorPortal = new TemplatePortal(
        this.popoverTrigger,
        this.vcr
      );
      this.overlayRef.attach(periodSelectorPortal);
    }
  }

  private detachOverlay(): void {
    if (this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }
}
